import { Injectable, signal } from '@angular/core';
import {
  Article,
  DictionaryEntry,
  Editor,
  LanguageCode,
  LinkItem,
  NavItem,
  StoredContent
} from './models';
import { DEFAULT_ARTICLES, DEFAULT_DICTIONARIES, DEFAULT_LINKS } from './default-content';

const STORAGE_KEY = 'ilmza-content-v1';

@Injectable({ providedIn: 'root' })
export class ContentService {
  readonly brand = 'علم زا';
  readonly tagline = 'سائنس اور علم — اردو اور علاقائی زبانوں میں';
  readonly email = 'yemeenzuberi9@gmail.com';

  readonly nav: NavItem[] = [
    {
      label: 'علم زا',
      route: '/',
      children: [{ label: 'حرفِ پاک', route: '/' }]
    },
    {
      label: 'زبان',
      route: '/languages',
      children: [
        { label: 'اردو', route: '/languages/urdu' },
        { label: 'پنجابی', route: '/languages/punjabi' },
        { label: 'سندھی', route: '/languages/sindhi' },
        { label: 'بلوچی', route: '/languages/balochi' },
        { label: 'پشتو', route: '/languages/pashto' }
      ]
    },
    { label: 'کَڑیاں', route: '/links' },
    { label: 'لغات', route: '/dictionaries' },
    { label: 'بلاگ', route: '/blog' }
  ];

  readonly editors: Editor[] = [
    { role: 'مدیر اعلیٰ', name: 'یمین الاسلام زبیری' },
    { role: 'مدیر اردو', name: 'شایذ لطیف' },
    { role: 'مدیر اردو', name: 'اظفر ناز' },
    { role: 'مدیر بلوچی', name: 'عزیز اللہ بلوچ' },
    { role: 'مدیر پنجابی', name: 'مقصود احمد' },
    { role: 'مدیر سندھی', name: 'شبیر سومرو' },
    { role: 'مدیر سندھی', name: 'ظہور بزدار' },
    { role: 'محاسب', name: 'حرا زبیری' },
    { role: 'مدیر پشتو', name: 'نقیب اللہ' }
  ];

  readonly audiences = [
    'اردو',
    'بلوچی',
    'پنجابی',
    'سندھی',
    'طلبہ',
    'عام آدمی',
    'گھر یلو خواتین',
    'علم کے شیدائی'
  ];

  readonly mailTopics = [
    'بلاگ',
    'غیر سیاسی مکتوب',
    'کہانیاں',
    'شاعری',
    'لطیفے',
    'پہیلیاں',
    'مضامین'
  ];

  readonly languageLabels: Record<LanguageCode, string> = {
    urdu: 'اردو',
    punjabi: 'پنجابی',
    sindhi: 'سندھی',
    balochi: 'بلوچی',
    pashto: 'پشتو'
  };

  readonly languageCodes = Object.keys(this.languageLabels) as LanguageCode[];

  readonly articles = signal<Article[]>([]);
  readonly links = signal<LinkItem[]>([]);
  readonly dictionaries = signal<DictionaryEntry[]>([]);

  constructor() {
    this.load();
  }

  getAll(): Article[] {
    return this.articles();
  }

  getBlog(): Article[] {
    return this.getAll();
  }

  getFeatured(): Article[] {
    return this.articles().filter((a) => a.featured);
  }

  getBySlug(slug: string): Article | undefined {
    return this.articles().find((a) => a.slug === slug);
  }

  getById(id: string): Article | undefined {
    return this.articles().find((a) => a.id === id);
  }

  getByLanguage(lang: LanguageCode): Article[] {
    return this.articles().filter((a) => a.language === lang);
  }

  getRelated(slug: string, limit = 3): Article[] {
    const current = this.getBySlug(slug);
    if (!current) {
      return this.articles().slice(0, limit);
    }
    return this.articles()
      .filter((a) => a.slug !== slug && a.category === current.category)
      .concat(this.articles().filter((a) => a.slug !== slug && a.category !== current.category))
      .slice(0, limit);
  }

  saveArticle(article: Article): void {
    const list = this.articles();
    const index = list.findIndex((a) => a.id === article.id);
    const next = index >= 0 ? list.map((a, i) => (i === index ? article : a)) : [article, ...list];
    this.articles.set(next);
    this.persist();
  }

  deleteArticle(id: string): void {
    this.articles.set(this.articles().filter((a) => a.id !== id));
    this.persist();
  }

  saveLink(link: LinkItem): void {
    const list = this.links();
    const index = list.findIndex((l) => l.id === link.id);
    const next = index >= 0 ? list.map((l, i) => (i === index ? link : l)) : [...list, link];
    this.links.set(next);
    this.persist();
  }

  deleteLink(id: string): void {
    this.links.set(this.links().filter((l) => l.id !== id));
    this.persist();
  }

  saveDictionary(entry: DictionaryEntry): void {
    const list = this.dictionaries();
    const index = list.findIndex((d) => d.id === entry.id);
    const next = index >= 0 ? list.map((d, i) => (i === index ? entry : d)) : [...list, entry];
    this.dictionaries.set(next);
    this.persist();
  }

  deleteDictionary(id: string): void {
    this.dictionaries.set(this.dictionaries().filter((d) => d.id !== id));
    this.persist();
  }

  resetToDefaults(): void {
    this.articles.set(structuredClone(DEFAULT_ARTICLES));
    this.links.set(structuredClone(DEFAULT_LINKS));
    this.dictionaries.set(structuredClone(DEFAULT_DICTIONARIES));
    this.persist();
  }

  createSlug(title: string): string {
    const base = title
      .trim()
      .toLowerCase()
      .replace(/[^\u0600-\u06FFa-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .slice(0, 60);
    const existing = new Set(this.articles().map((a) => a.slug));
    if (!existing.has(base)) {
      return base || `article-${Date.now()}`;
    }
    let i = 2;
    while (existing.has(`${base}-${i}`)) {
      i++;
    }
    return `${base}-${i}`;
  }

  newId(prefix: string): string {
    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
  }

  private load(): void {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      try {
        const data = JSON.parse(raw) as StoredContent;
        this.articles.set(data.articles ?? structuredClone(DEFAULT_ARTICLES));
        this.links.set(data.links ?? structuredClone(DEFAULT_LINKS));
        this.dictionaries.set(data.dictionaries ?? structuredClone(DEFAULT_DICTIONARIES));
        return;
      } catch {
        /* fall through to defaults */
      }
    }
    this.articles.set(structuredClone(DEFAULT_ARTICLES));
    this.links.set(structuredClone(DEFAULT_LINKS));
    this.dictionaries.set(structuredClone(DEFAULT_DICTIONARIES));
    this.persist();
  }

  private persist(): void {
    const payload: StoredContent = {
      articles: this.articles(),
      links: this.links(),
      dictionaries: this.dictionaries()
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  }
}
